import React from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import Client from './components/Client';
import Config from './components/ClientConfig';

const CREDIT_TYPES = {
  0: 'Reward',
  1: 'Bonus',
  2: 'Used',
  3: 'Sent',
  4: 'Manual add',
  5: 'Manual remove',
  6: 'Refund',
  9: 'Undefined',
};

const STATUS_LABELS = {
  0: 'Registered',
  1: 'Complete',
  2: 'Complete with errors',
  3: 'Cancelled',
  4: 'InSync',
  5: 'Paid',
  6: 'Accepted',
  7: 'Ready',
  8: 'Rejected',
  9: 'Undefined',
};

const ADJUSTMENT_REASONS = [
  'Customer compensation',
  'Promotion correction',
  'Refund correction',
  'Operational mistake',
  'Abuse / invalid credit',
  'Other',
];

const PERIOD_LABELS = {
  today: 'Today',
  last_7d: '7d',
  last_30d: '30d',
  this_month: 'Month',
  last_90d: '90d',
};

const CREDIT_TIME_ZONE = 'Europe/Bucharest';

function validDateTimeParts(year, month, day, hour, minute, second) {
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
    && date.getUTCHours() === hour
    && date.getUTCMinutes() === minute
    && date.getUTCSeconds() === second;
}

function displayDateTimeParts(year, month, day, hour, minute) {
  return [
    String(day).padStart(2, '0'),
    String(month).padStart(2, '0'),
    String(year).padStart(4, '0'),
  ].join('-') + ` ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function formatZonedDateTime(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: CREDIT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date).reduce((result, part) => {
    if (part.type !== 'literal') result[part.type] = part.value;
    return result;
  }, {});

  return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute}`;
}

export function formatCreditDateTime(value) {
  if (!value) return '-';

  const rawValue = String(value).trim();
  const europeanMatch = rawValue.match(
    /^(\d{1,2})-(\d{1,2})-(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (europeanMatch) {
    const day = Number(europeanMatch[1]);
    const month = Number(europeanMatch[2]);
    const year = Number(europeanMatch[3]);
    const hour = Number(europeanMatch[4] || 0);
    const minute = Number(europeanMatch[5] || 0);
    const second = Number(europeanMatch[6] || 0);
    if (validDateTimeParts(year, month, day, hour, minute, second)) {
      return displayDateTimeParts(year, month, day, hour, minute);
    }
    return rawValue;
  }

  const localIsoMatch = rawValue.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/,
  );
  if (localIsoMatch) {
    const year = Number(localIsoMatch[1]);
    const month = Number(localIsoMatch[2]);
    const day = Number(localIsoMatch[3]);
    const hour = Number(localIsoMatch[4] || 0);
    const minute = Number(localIsoMatch[5] || 0);
    const second = Number(localIsoMatch[6] || 0);
    if (validDateTimeParts(year, month, day, hour, minute, second)) {
      return displayDateTimeParts(year, month, day, hour, minute);
    }
    return rawValue;
  }

  const date = new Date(rawValue);
  if (Number.isNaN(date.getTime())) return rawValue;
  return formatZonedDateTime(date);
}

class ClientCreditManagement extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      clientApi: new Client('none'),

      email: '',
      searchEmail: '',
      selectedClient: null,

      periodMode: 'last_30d',
      history: [],
      historyFilter: 'all',

      allClientSummary: {
        rewarded: 0,
        used: 0,
        net: 0,
        transactions: 0,
      },
      allClientDailyRows: [],

      action: 'add',
      value: '',
      reason: '',
      note: '',

      loadingClient: false,
      loadingHistory: false,
      saving: false,

      error: '',
      success: '',
    };
  }

    componentDidMount() {
      this.loadAllClientSummary();

      if (this.props.onReady) {
        this.props.onReady();
      }
    }

  formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  getPeriodRange = (periodMode) => {
    const dateTo = new Date();
    const dateFrom = new Date();

    if (periodMode === 'today') {
      // Today only
    } else if (periodMode === 'last_7d') {
      dateFrom.setDate(dateTo.getDate() - 7);
    } else if (periodMode === 'last_90d') {
      dateFrom.setDate(dateTo.getDate() - 90);
    } else if (periodMode === 'this_month') {
      dateFrom.setDate(1);
    } else {
      dateFrom.setDate(dateTo.getDate() - 30);
    }

    return {
      date_from: this.formatDate(dateFrom),
      date_to: this.formatDate(dateTo),
    };
  };

  formatMoney = (value) => {
    const numberValue = Number(value || 0);

    return numberValue.toLocaleString('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  formatDateTime = (value) => {
    return formatCreditDateTime(value);
  };

  getResponseError = (response, fallback = 'Request failed.') => {
    if (!response) return fallback;

    if (response.detail) return response.detail;
    if (response.message) return response.message;
    if (response.error) return response.error;

    if (response.status === 'ERROR' && response.message) {
      return response.message;
    }

    return fallback;
  };

  normalizeClientResponse = (response) => {
    if (!response) return null;

    let data = response.Client || response.client_data || response.clientDetails || response;

    if (typeof data !== 'object') {
      data = response;
    }

    if (!data) return null;

    return {
      iref: data.iref || data.client || data.client_iref || '',
      name: data.name || data.client_name || data.fullName || '',
      email: data.email || data.client_email || '',
      phone: data.phone || data.mobile || data.client_phone || '',
      credit: Number(data.credit || data.currentCredit || data.current_credit || 0),
    };
  };

  getRowType = (row) => Number(row.type);

  getRowValue = (row) => Number(row.value || 0);

  isManualRow = (row) => [4, 5].includes(this.getRowType(row));

  isRewardedRow = (row) => [0, 1].includes(this.getRowType(row));

  isUsedRow = (row) => this.getRowType(row) === 2;

  getFilteredHistory = () => {
    const { history, historyFilter } = this.state;

    if (historyFilter === 'rewarded') {
      return history.filter((row) => this.isRewardedRow(row));
    }

    if (historyFilter === 'used') {
      return history.filter((row) => this.isUsedRow(row));
    }

    if (historyFilter === 'manual') {
      return history.filter((row) => this.isManualRow(row));
    }

    return history;
  };

  getHistorySummary = (rows = this.state.history) => {
    const rewarded = rows
      .filter((row) => this.isRewardedRow(row))
      .reduce((sum, row) => sum + Math.abs(this.getRowValue(row)), 0);

    const used = rows
      .filter((row) => this.isUsedRow(row))
      .reduce((sum, row) => sum + Math.abs(this.getRowValue(row)), 0);

    const net = rows.reduce((sum, row) => sum + this.getRowValue(row), 0);

    return {
      rewarded,
      used,
      net,
      transactions: rows.length,
    };
  };

  getDailySummaryRows = () => {
    if (!this.state.selectedClient) {
      return (this.state.allClientDailyRows || [])
        .slice()
        .sort((a, b) => {
          if (a.date === 'Unknown') return 1;
          if (b.date === 'Unknown') return -1;

          return new Date(b.date).getTime() - new Date(a.date).getTime();
        });
    }

    const grouped = {};

    const getDateKey = (row) => {
      const rawValue = row.datetime || row.createdOn || '';

      if (!rawValue) {
        return 'Unknown';
      }

      const value = String(rawValue).trim();

      // Backend serializer format: dd-mm-yyyy HH:mm:ss
      const europeanDateMatch = value.match(/^(\d{2})-(\d{2})-(\d{4})/);

      if (europeanDateMatch) {
        const day = europeanDateMatch[1];
        const month = europeanDateMatch[2];
        const year = europeanDateMatch[3];

        return `${year}-${month}-${day}`;
      }

      // ISO format fallback: yyyy-mm-dd...
      const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

      if (isoDateMatch) {
        return `${isoDateMatch[1]}-${isoDateMatch[2]}-${isoDateMatch[3]}`;
      }

      return value.substring(0, 10) || 'Unknown';
    };

    this.state.history.forEach((row) => {
      const dateKey = getDateKey(row);

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          rewarded: 0,
          used: 0,
          net: 0,
          transactions: 0,
        };
      }

      const rowValue = this.getRowValue(row);

      if (this.isRewardedRow(row)) {
        grouped[dateKey].rewarded += Math.abs(rowValue);
      }

      if (this.isUsedRow(row)) {
        grouped[dateKey].used += Math.abs(rowValue);
      }

      grouped[dateKey].net += rowValue;
      grouped[dateKey].transactions += 1;
    });

    return Object.values(grouped)
      .sort((a, b) => {
        if (a.date === 'Unknown') return 1;
        if (b.date === 'Unknown') return -1;

        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
  };

  getEvolutionRows = () => {
    return this.getDailySummaryRows()
      .slice(0, 14);
  };

  getClientTransactionRows = () => {
    return this.getFilteredHistory().slice(0, 20);
  };

  getHistoryQuery = (email = null, periodMode = null) => {
    const cleanEmail = String(email || '').trim().toLowerCase();
    const selectedPeriod = periodMode || this.state.periodMode;
    const range = this.getPeriodRange(selectedPeriod);

    const query = {
      date_from: range.date_from,
      date_to: range.date_to,
      orderBy_datetime: 1,
      limit: cleanEmail ? 50 : 500,
    };

    if (cleanEmail) {
      query.client_email = cleanEmail;
    }

    return query;
  };

  loadClientCredit = (email) => {
    const cleanEmail = String(email || '').trim().toLowerCase();

    if (!cleanEmail) {
      this.setState({
        error: 'Email is required.',
        selectedClient: null,
        searchEmail: '',
      });
      return null;
    }

    this.setState({
      loadingClient: true,
      error: '',
      success: '',
    });

    const response = this.state.clientApi.getData(
      Config.API_URL_CLIENT + 'getClientCreditByEmail/',
      {
        email: cleanEmail,
      }
    );

    if (!response) {
      this.setState({
        loadingClient: false,
        selectedClient: null,
        error: 'Could not load client.',
      });
      return null;
    }

    if (response.status === 'ERROR') {
      this.setState({
        loadingClient: false,
        selectedClient: null,
        error: this.getResponseError(response, 'Could not load client.'),
      });
      return null;
    }

    const selectedClient = this.normalizeClientResponse(response);

    if (!selectedClient || !selectedClient.email) {
      this.setState({
        loadingClient: false,
        selectedClient: null,
        error: 'Client not found or invalid response.',
      });
      return null;
    }

    this.setState({
      selectedClient,
      searchEmail: cleanEmail,
      loadingClient: false,
      historyFilter: 'all',
    });

    return selectedClient;
  };

  loadAllClientSummary = (periodMode = null) => {
    const selectedPeriod = periodMode || this.state.periodMode;
    const range = this.getPeriodRange(selectedPeriod);

    this.setState({
      loadingHistory: true,
      error: '',
    });

    const response = this.state.clientApi.getData(
      Config.API_URL_CLIENT_CREDIT + 'dailySummary/',
      {
        date_from: range.date_from,
        date_to: range.date_to,
      }
    );

    if (!response) {
      this.setState({
        loadingHistory: false,
        history: [],
        allClientSummary: {
          rewarded: 0,
          used: 0,
          net: 0,
          transactions: 0,
        },
        allClientDailyRows: [],
        error: 'Could not load credit summary.',
      });
      return;
    }

    if (response.status === 'ERROR') {
      this.setState({
        loadingHistory: false,
        history: [],
        allClientSummary: {
          rewarded: 0,
          used: 0,
          net: 0,
          transactions: 0,
        },
        allClientDailyRows: [],
        error: this.getResponseError(response, 'Could not load credit summary.'),
      });
      return;
    }

    this.setState({
      loadingHistory: false,
      history: [],
      allClientSummary: response.summary || {
        rewarded: 0,
        used: 0,
        net: 0,
        transactions: 0,
      },
      allClientDailyRows: response.daily_rows || [],
    });
  };

  loadCreditHistory = (email = null, periodMode = null) => {
    const cleanEmail = String(email || '').trim().toLowerCase();
    const query = this.getHistoryQuery(cleanEmail, periodMode);

    this.setState({
      loadingHistory: true,
      error: '',
    });

    const response = this.state.clientApi.getData(
      Config.API_URL_CLIENT_CREDIT,
      query
    );

    if (!response) {
      this.setState({
        loadingHistory: false,
        history: [],
        error: 'Could not load credit history.',
      });
      return;
    }

    if (response.status === 'ERROR') {
      this.setState({
        loadingHistory: false,
        history: [],
        error: this.getResponseError(response, 'Could not load credit history.'),
      });
      return;
    }

    this.setState({
      loadingHistory: false,
      history: response.ClientCreditHistory || response.results || response.history || [],
    });
  };

  handleSearchClient = () => {
    const { email, periodMode } = this.state;

    const selectedClient = this.loadClientCredit(email);

    if (selectedClient) {
      this.loadCreditHistory(selectedClient.email || email, periodMode);
    }
  };

  handleClearClient = () => {
    this.setState({
      email: '',
      searchEmail: '',
      selectedClient: null,
      value: '',
      reason: '',
      note: '',
      action: 'add',
      success: '',
      error: '',
      historyFilter: 'all',
      history: [],
    }, () => {
      this.loadAllClientSummary(this.state.periodMode);
    });
  };

  handlePeriodChange = (event, newPeriodMode) => {
    if (!newPeriodMode) return;

    this.setState({
      periodMode: newPeriodMode,
    }, () => {
      if (this.state.selectedClient || this.state.searchEmail) {
        this.loadCreditHistory(this.state.searchEmail, newPeriodMode);
      } else {
        this.loadAllClientSummary(newPeriodMode);
      }
    });
  };

  handleAdjustCredit = () => {
    const {
      selectedClient,
      searchEmail,
      action,
      value,
      reason,
      note,
    } = this.state;

    if (!selectedClient || !searchEmail) {
      this.setState({
        error: 'Search and select a client first.',
        success: '',
      });
      return;
    }

    const numericValue = Number(value);

    if (!numericValue || numericValue <= 0) {
      this.setState({
        error: 'Amount must be greater than 0.',
        success: '',
      });
      return;
    }

    if (!String(reason || '').trim()) {
      this.setState({
        error: 'Reason is required.',
        success: '',
      });
      return;
    }

    if (!String(note || '').trim()) {
      this.setState({
        error: 'Internal note is required.',
        success: '',
      });
      return;
    }

    if (action === 'remove' && numericValue > Number(selectedClient.credit || 0)) {
      this.setState({
        error: 'Cannot remove more credit than the client currently has.',
        success: '',
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    const response = this.state.clientApi.pushData(
      Config.API_URL_CLIENT + 'adjustClientCredit/',
      {
        email: searchEmail,
        action,
        value: numericValue,
        reason,
        note: `${reason} - ${String(note || '').trim()}`,
      }
    );

    if (!response) {
      this.setState({
        saving: false,
        error: 'Could not update client credit.',
      });
      return;
    }

    if (response.status === 'ERROR') {
      this.setState({
        saving: false,
        error: this.getResponseError(response, 'Could not update client credit.'),
      });
      return;
    }

    this.loadClientCredit(searchEmail);
    this.loadCreditHistory(searchEmail);

    this.setState({
      saving: false,
      value: '',
      reason: '',
      note: '',
      success: 'Client credit updated successfully.',
    });
  };

  renderCompactMetric = (title, value, subtitle, tone = 'default') => {
    const toneSx = {
      default: '#020617',
      positive: '#166534',
      negative: '#991b1b',
      neutral: '#1e3a8a',
    };

    return (
      <Box
        sx={{
          minWidth: 0,
          px: { xs: 1, sm: 1.25 },
          py: { xs: 0.75, sm: 0.9 },
          borderRadius: '14px',
          bgcolor: '#f8fafc',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          height: '100%',
        }}
      >
        <Typography
          sx={{
            color: '#64748b',
            fontSize: { xs: '0.66rem', sm: '0.7rem' },
            fontWeight: 800,
            lineHeight: 1.15,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            color: toneSx[tone] || toneSx.default,
            fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.12rem' },
            fontWeight: 950,
            lineHeight: 1.15,
            mt: 0.25,
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>

        {subtitle ? (
          <Typography
            sx={{
              color: '#94a3b8',
              fontSize: { xs: '0.6rem', sm: '0.65rem' },
              lineHeight: 1.15,
              mt: 0.25,
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    );
  };

  renderPanelHeader = (title, subtitle, action = null) => (
    <Box
      sx={{
        p: { xs: 1.25, sm: 1.5, md: 1.75 },
        pb: 1.1,
        flexShrink: 0,
        borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        <Box
          sx={{
            minWidth: 0,
            textAlign: 'left',
          }}
        >
          <Typography
            sx={{
              fontWeight: 950,
              color: '#020617',
              textAlign: 'left',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              fontSize: '0.76rem',
              textAlign: 'left',
              lineHeight: 1.35,
              mt: 0.25,
            }}
          >
            {subtitle}
          </Typography>
        </Box>

        {action ? (
          <Box
            sx={{
              flexShrink: 0,
              pt: 0,
            }}
          >
            {action}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );

  renderEvolutionChart = () => {
    const rows = this.getEvolutionRows();
    const maxValue = Math.max(
      1,
      ...rows.map((row) => Math.max(row.rewarded, row.used, Math.abs(row.net)))
    );

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: '14px', sm: '16px' },
          border: '1px solid rgba(15, 23, 42, 0.06)',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
          bgcolor: '#ffffff',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {this.renderPanelHeader(
          'Credit evolution',
          'Rewarded versus used credit'
        )}

        <Box
          sx={{
            p: { xs: 1.25, sm: 1.5, md: 1.75 },
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {rows.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px dashed rgba(100, 116, 139, 0.3)',
                borderRadius: '14px',
                bgcolor: '#f8fafc',
              }}
            >
              <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center' }}>
                No credit movements found for this period.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0.85} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.4 }}>
              {rows.map((row) => {
                const rewardedWidth = Math.max(2, (row.rewarded / maxValue) * 100);
                const usedWidth = Math.max(2, (row.used / maxValue) * 100);

                return (
                  <Box key={row.date}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.35 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 800 }}>
                        {row.date}
                      </Typography>

                      <Typography sx={{ fontSize: '0.72rem', color: '#334155', fontWeight: 800 }}>
                        Net {row.net >= 0 ? '+' : ''}{this.formatMoney(row.net)}
                      </Typography>
                    </Stack>

                    <Stack spacing={0.35}>
                      <Box sx={{ height: 8, bgcolor: '#ecfdf5', borderRadius: 99, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            width: `${rewardedWidth}%`,
                            height: '100%',
                            bgcolor: '#22c55e',
                            borderRadius: 99,
                          }}
                        />
                      </Box>

                      <Box sx={{ height: 8, bgcolor: '#fef2f2', borderRadius: 99, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            width: `${usedWidth}%`,
                            height: '100%',
                            bgcolor: '#ef4444',
                            borderRadius: 99,
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        <Stack
          component="footer"
          aria-label="Credit evolution legend"
          direction="row"
          spacing={0.75}
          sx={{
            px: { xs: 1.25, sm: 1.5, md: 1.75 },
            py: 1,
            flexShrink: 0,
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#ffffff',
          }}
        >
          <Chip
            size="small"
            label="Rewarded"
            sx={{ bgcolor: '#f0fdf4', color: '#166534', fontWeight: 800 }}
          />
          <Chip
            size="small"
            label="Used"
            sx={{ bgcolor: '#fef2f2', color: '#991b1b', fontWeight: 800 }}
          />
        </Stack>
      </Paper>
    );
  };

  renderClientPanel = (clientSummary) => {
    const {
      email,
      selectedClient,
      action,
      value,
      reason,
      note,
      loadingClient,
      saving,
    } = this.state;

    const numericValue = Number(value || 0);
    const signedAdjustment = action === 'remove' ? -numericValue : numericValue;
    const newBalance = selectedClient
      ? Number(selectedClient.credit || 0) + signedAdjustment
      : 0;

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: '14px', sm: '16px' },
          border: '1px solid rgba(15, 23, 42, 0.06)',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
          bgcolor: '#ffffff',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {this.renderPanelHeader(
          'Client management',
          'Search client and adjust credit',
          selectedClient ? (
            <Button
              size="small"
              variant="text"
              onClick={this.handleClearClient}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                color: '#475569',
                flexShrink: 0,
              }}
            >
              Clear
            </Button>
          ) : null
        )}

        <Box
          sx={{
            p: { xs: 1.25, sm: 1.5, md: 1.75 },
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.25 }}>
            <TextField
              fullWidth
              size="small"
              label="Client email"
              value={email}
              onChange={(event) => this.setState({ email: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  this.handleSearchClient();
                }
              }}
            />

            <Button
              variant="contained"
              onClick={this.handleSearchClient}
              disabled={loadingClient}
              sx={{
                minWidth: { xs: '100%', sm: 110 },
                bgcolor: '#0f172a',
                textTransform: 'none',
                fontWeight: 900,
                borderRadius: '10px',

                '&:hover': {
                  bgcolor: '#111827',
                },
              }}
            >
              {loadingClient ? <CircularProgress size={20} color="inherit" /> : 'Search'}
            </Button>
          </Stack>

          {!selectedClient ? (
            <Box
              sx={{
                p: 1.35,
                borderRadius: '14px',
                bgcolor: '#f8fafc',
                border: '1px dashed rgba(100, 116, 139, 0.3)',
                mb: 1.25,
              }}
            >
              <Typography sx={{ fontWeight: 850, color: '#334155', mb: 0.35 }}>
                No client selected
              </Typography>

              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.76rem' }}>
                The report shows all clients. Search by email to manage one client.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                p: 1.25,
                borderRadius: '14px',
                bgcolor: '#f8fafc',
                border: '1px solid rgba(15, 23, 42, 0.06)',
                mb: 1.25,
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 950, color: '#020617', lineHeight: 1.15 }}>
                    {selectedClient.name || 'Client'}
                  </Typography>

                  <Typography sx={{ color: '#64748b', fontSize: '0.74rem', wordBreak: 'break-word' }}>
                    {selectedClient.email || '-'}
                  </Typography>
                </Box>

                <Chip
                  label="Selected"
                  size="small"
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#1e3a8a',
                    fontWeight: 900,
                    flexShrink: 0,
                  }}
                />
              </Stack>

              <Divider sx={{ my: 1 }} />

              <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={1}>
                <Box>
                  <Typography sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 800 }}>
                    Current balance
                  </Typography>

                  <Typography
                    sx={{
                      color: Number(selectedClient.credit || 0) > 0 ? '#166534' : '#020617',
                      fontSize: { xs: '1.55rem', md: '1.75rem' },
                      fontWeight: 950,
                      lineHeight: 1.05,
                    }}
                  >
                    {this.formatMoney(selectedClient.credit)}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>
                    {PERIOD_LABELS[this.state.periodMode]}
                  </Typography>

                  <Typography sx={{ color: '#166534', fontSize: '0.75rem', fontWeight: 900 }}>
                    Rewarded {this.formatMoney(clientSummary.rewarded)}
                  </Typography>

                  <Typography sx={{ color: '#991b1b', fontSize: '0.75rem', fontWeight: 900 }}>
                    Used {this.formatMoney(clientSummary.used)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          <Typography sx={{ fontWeight: 950, color: '#020617', mb: 1 }}>
            Manual adjustment
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Type"
                value={action}
                disabled={!selectedClient || saving}
                onChange={(event) => this.setState({ action: event.target.value })}
              >
                <MenuItem value="add">Add credit</MenuItem>
                <MenuItem value="remove">Remove credit</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Amount"
                value={value}
                disabled={!selectedClient || saving}
                onChange={(event) => this.setState({ value: event.target.value })}
                inputProps={{
                  min: 0,
                  step: '0.01',
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Reason"
                value={reason}
                disabled={!selectedClient || saving}
                onChange={(event) => this.setState({ reason: event.target.value })}
              >
                {ADJUSTMENT_REASONS.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Internal note"
                value={note}
                disabled={!selectedClient || saving}
                onChange={(event) => this.setState({ note: event.target.value })}
                inputProps={{
                  maxLength: 255,
                }}
                helperText={`${String(note || '').length}/255`}
              />
            </Grid>
          </Grid>

          {selectedClient && numericValue > 0 ? (
            <Box
              sx={{
                mt: 1,
                p: 1.15,
                borderRadius: '14px',
                bgcolor: action === 'remove' ? '#fffbeb' : '#f8fafc',
                border: '1px solid rgba(15, 23, 42, 0.06)',
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 800 }}>
                  Current balance
                </Typography>

                <Typography sx={{ color: '#334155', fontSize: '0.75rem', fontWeight: 900 }}>
                  {this.formatMoney(selectedClient.credit)}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 800 }}>
                  Adjustment
                </Typography>

                <Typography
                  sx={{
                    color: action === 'remove' ? '#991b1b' : '#166534',
                    fontSize: '0.75rem',
                    fontWeight: 950,
                  }}
                >
                  {signedAdjustment >= 0 ? '+' : ''}{this.formatMoney(signedAdjustment)}
                </Typography>
              </Stack>

              <Divider sx={{ my: 0.6 }} />

              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography sx={{ color: '#020617', fontSize: '0.8rem', fontWeight: 950 }}>
                  New balance
                </Typography>

                <Typography
                  sx={{
                    color: newBalance >= 0 ? '#020617' : '#991b1b',
                    fontSize: '0.8rem',
                    fontWeight: 950,
                  }}
                >
                  {this.formatMoney(newBalance)}
                </Typography>
              </Stack>
            </Box>
          ) : null}

          {action === 'remove' && selectedClient ? (
            <Alert severity="warning" sx={{ mt: 1, borderRadius: '12px', fontSize: '0.78rem' }}>
              Removing credit reduces the client&apos;s available balance.
            </Alert>
          ) : null}
        </Box>

        <Box
          sx={{
            p: { xs: 1.25, sm: 1.5, md: 1.75 },
            pt: 1,
            flexShrink: 0,
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#ffffff',
          }}
        >
          <Button
            fullWidth
            variant="contained"
            disabled={!selectedClient || saving}
            onClick={this.handleAdjustCredit}
            sx={{
              height: 42,
              bgcolor: action === 'remove' ? '#991b1b' : '#0f172a',
              textTransform: 'none',
              fontWeight: 950,
              borderRadius: '12px',

              '&:hover': {
                bgcolor: action === 'remove' ? '#7f1d1d' : '#111827',
              },
            }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : action === 'remove' ? (
              'Confirm remove credit'
            ) : (
              'Confirm add credit'
            )}
          </Button>
        </Box>
      </Paper>
    );
  };

  renderDailySummary = () => {
    const rows = this.getDailySummaryRows();

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: '14px', sm: '16px' },
          border: '1px solid rgba(15, 23, 42, 0.06)',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
          bgcolor: '#ffffff',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {this.renderPanelHeader(
          'Daily credit summary',
          `All clients · ${PERIOD_LABELS[this.state.periodMode]}`
        )}

        <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 1.75 }, pt: 1.25, flex: 1, minHeight: 0 }}>
          <TableContainer
            sx={{
              height: '100%',
              border: '1px solid rgba(15, 23, 42, 0.06)',
              borderRadius: '12px',
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Rewarded</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Used</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Net</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Transactions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" sx={{ py: 3, textAlign: 'center', color: '#64748b' }}>
                        No credit activity found for the selected period.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}

                {rows.map((row) => (
                  <TableRow key={row.date} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: '#334155', fontWeight: 800 }}>
                      {row.date}
                    </TableCell>

                    <TableCell align="right" sx={{ color: '#166534', fontWeight: 950, whiteSpace: 'nowrap' }}>
                      {this.formatMoney(row.rewarded)}
                    </TableCell>

                    <TableCell align="right" sx={{ color: '#991b1b', fontWeight: 950, whiteSpace: 'nowrap' }}>
                      {this.formatMoney(row.used)}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        color: row.net >= 0 ? '#166534' : '#991b1b',
                        fontWeight: 950,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.net >= 0 ? '+' : ''}{this.formatMoney(row.net)}
                    </TableCell>

                    <TableCell align="right" sx={{ color: '#334155', fontWeight: 850 }}>
                      {row.transactions}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    );
  };

  renderClientTransactions = () => {
    const {
      history,
      loadingHistory,
      selectedClient,
      searchEmail,
      historyFilter,
    } = this.state;

    const rows = this.getClientTransactionRows();

    const filters = [
      { value: 'all', label: 'All' },
      { value: 'rewarded', label: 'Rewarded' },
      { value: 'used', label: 'Used' },
      { value: 'manual', label: 'Manual' },
    ];

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: '14px', sm: '16px' },
          border: '1px solid rgba(15, 23, 42, 0.06)',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
          bgcolor: '#ffffff',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {this.renderPanelHeader(
          'Client credit history',
          `${searchEmail || selectedClient?.email || 'Selected client'} · last 20 transactions`,
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ overflowX: 'auto' }}>
            {filters.map((item) => (
              <Chip
                key={item.value}
                size="small"
                label={item.label}
                clickable
                onClick={() => this.setState({ historyFilter: item.value })}
                sx={{
                  fontWeight: 850,
                  bgcolor: historyFilter === item.value ? '#0f172a' : '#f1f5f9',
                  color: historyFilter === item.value ? '#ffffff' : '#334155',
                  flexShrink: 0,
                }}
              />
            ))}

            {loadingHistory ? (
              <CircularProgress size={20} />
            ) : (
              <Chip
                size="small"
                label={`${rows.length}/${history.length}`}
                sx={{ fontWeight: 850, bgcolor: '#eff6ff', color: '#1e3a8a', flexShrink: 0 }}
              />
            )}
          </Stack>
        )}

        <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 1.75 }, pt: 1.25, flex: 1, minHeight: 0 }}>
          <Box sx={{ display: { xs: 'none', md: 'block' }, height: '100%' }}>
            <TableContainer
              sx={{
                height: '100%',
                border: '1px solid rgba(15, 23, 42, 0.06)',
                borderRadius: '12px',
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Type</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Order</TableCell>
                    <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Created by</TableCell>
                    <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Note</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loadingHistory && rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" sx={{ py: 3, textAlign: 'center', color: '#64748b' }}>
                          No client transactions found for the selected filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {rows.map((row, index) => {
                    const rowValue = this.getRowValue(row);
                    const isPositive = rowValue > 0;
                    const isNegative = rowValue < 0;
                    const rowType = this.getRowType(row);

                    return (
                      <TableRow key={row.iref || `${row.datetime}-${index}`} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: '#334155' }}>
                          {this.formatDateTime(row.datetime || row.createdOn)}
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={CREDIT_TYPES[rowType] || row.type || '-'}
                            sx={{
                              fontWeight: 850,
                              bgcolor: isPositive ? '#f0fdf4' : isNegative ? '#fef2f2' : '#f1f5f9',
                              color: isPositive ? '#166534' : isNegative ? '#991b1b' : '#334155',
                            }}
                          />
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 950,
                            color: isPositive ? '#166534' : isNegative ? '#991b1b' : '#334155',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isPositive ? '+' : ''}{this.formatMoney(rowValue)}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {row.order || '-'}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {row.createdBy_name || '-'}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {STATUS_LABELS[row.status] || row.status || '-'}
                        </TableCell>

                        <TableCell sx={{ minWidth: 220 }}>
                          {row.note || '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Stack
            spacing={1}
            sx={{
              display: { xs: 'flex', md: 'none' },
              height: '100%',
              minHeight: 0,
              overflowY: 'auto',
              pr: 0.25,
            }}
          >
            {!loadingHistory && rows.length === 0 ? (
              <Box
                sx={{
                  py: 3,
                  textAlign: 'center',
                  color: '#64748b',
                  bgcolor: '#f8fafc',
                  borderRadius: '14px',
                }}
              >
                <Typography variant="body2">
                  No client transactions found for the selected filters.
                </Typography>
              </Box>
            ) : null}

            {rows.map((row, index) => {
              const rowValue = this.getRowValue(row);
              const isPositive = rowValue > 0;
              const isNegative = rowValue < 0;
              const rowType = this.getRowType(row);

              return (
                <Paper
                  key={row.iref || `${row.datetime}-${index}`}
                  elevation={0}
                  sx={{
                    p: 1.1,
                    borderRadius: '14px',
                    border: '1px solid rgba(15, 23, 42, 0.06)',
                    bgcolor: '#f8fafc',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.6 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 950, color: '#020617', fontSize: '0.86rem' }}>
                        {CREDIT_TYPES[rowType] || row.type || '-'}
                      </Typography>

                      <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                        {this.formatDateTime(row.datetime || row.createdOn)}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontWeight: 950,
                        color: isPositive ? '#166534' : isNegative ? '#991b1b' : '#334155',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {isPositive ? '+' : ''}{this.formatMoney(rowValue)}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap', rowGap: 0.6, mb: row.note ? 0.65 : 0 }}>
                    <Chip size="small" label={row.order ? `Order ${row.order}` : this.isManualRow(row) ? 'Manual' : 'System'} />
                    <Chip size="small" label={STATUS_LABELS[row.status] || row.status || 'Status -'} />

                    {row.createdBy_name ? (
                      <Chip size="small" label={row.createdBy_name} />
                    ) : null}
                  </Stack>

                  {row.note ? (
                    <Typography sx={{ color: '#475569', fontSize: '0.75rem' }}>
                      {row.note}
                    </Typography>
                  ) : null}
                </Paper>
              );
            })}
          </Stack>
        </Box>
      </Paper>
    );
  };

  renderActivitySection = () => {
    const { selectedClient } = this.state;

    if (selectedClient) {
      return this.renderClientTransactions();
    }

    return this.renderDailySummary();
  };

  render() {
    const {
      selectedClient,
      periodMode,
      loadingHistory,
      error,
      success,
    } = this.state;

    const summary = selectedClient
      ? this.getHistorySummary(this.state.history)
      : this.state.allClientSummary;
    const clientSummary = selectedClient
      ? this.getHistorySummary(this.state.history)
      : this.state.allClientSummary;

    const pageSx = {
      height: {
        xs: 'auto',
        md: 'calc(100vh - 88px)',
      },
      minHeight: {
        xs: '100vh',
        md: 640,
      },
      flexGrow: 1,
      width: '100%',
      maxWidth: '100%',
      ml: 0,
      overflow: {
        xs: 'visible',
        md: 'hidden',
      },
      pt: { xs: 0.75, sm: 1.5, md: 2 },
      pb: { xs: 1, sm: 2, md: 2 },
      px: { xs: 0.5, sm: 1, md: 2 },
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',

      '& .MuiGrid-item': {
        minWidth: 0,
      },
    };

    const headerSx = {
      width: '100%',
      minWidth: 0,
      p: { xs: 1.25, sm: 1.5, md: 1.75 },
      borderRadius: { xs: '14px', sm: '16px' },
      border: '1px solid rgba(15, 23, 42, 0.06)',
      boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
      bgcolor: '#ffffff',
      boxSizing: 'border-box',
      flexShrink: 0,
    };

    return (
      <Box component="main" sx={pageSx}>
        <Stack
          spacing={{ xs: 0.75, md: 1.25 }}
          sx={{
            height: '100%',
            minHeight: 0,
          }}
        >
          <Paper elevation={0} sx={headerSx}>
            <Grid container spacing={{ xs: 1, md: 1.25 }} alignItems="center">
              <Grid item xs={12} lg={3}>
                <Box
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    textAlign: 'left',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-start"
                    sx={{
                      mb: 0.25,
                      width: '100%',
                      textAlign: 'left',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 950,
                        fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                        lineHeight: 1.2,
                        color: '#020617',
                        textAlign: 'left',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Credit Management
                    </Typography>

                    {loadingHistory ? <CircularProgress size={18} /> : null}
                  </Stack>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#475569',
                      fontSize: { xs: '0.72rem', sm: '0.78rem' },
                      lineHeight: 1.25,
                      textAlign: 'left',
                      width: '100%',
                    }}
                  >
                    Rewarded, used, and client credit control.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Grid container spacing={0.75}>
                  <Grid item xs={6} sm={3}>
                    {this.renderCompactMetric(
                      'Rewarded',
                      this.formatMoney(summary.rewarded),
                      'credit received',
                      'positive'
                    )}
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    {this.renderCompactMetric(
                      'Used',
                      this.formatMoney(summary.used),
                      'credit spent',
                      'negative'
                    )}
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    {this.renderCompactMetric(
                      'Net',
                      `${summary.net >= 0 ? '+' : ''}${this.formatMoney(summary.net)}`,
                      'movement',
                      summary.net >= 0 ? 'positive' : 'negative'
                    )}
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    {this.renderCompactMetric(
                      'Transactions',
                      summary.transactions,
                      selectedClient ? 'client view' : 'all clients',
                      'neutral'
                    )}
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={3}>
                <Stack
                  direction={{ xs: 'column', sm: 'row', lg: 'column' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center', lg: 'flex-end' }}
                >
                  <Chip
                    label={selectedClient ? 'Selected client' : 'All clients'}
                    sx={{
                      fontWeight: 900,
                      bgcolor: selectedClient ? '#eff6ff' : '#f1f5f9',
                      color: selectedClient ? '#1e3a8a' : '#334155',
                      alignSelf: { xs: 'flex-start', sm: 'center', lg: 'flex-end' },
                    }}
                  />

                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={periodMode}
                    onChange={this.handlePeriodChange}
                    sx={{
                      alignSelf: { xs: 'flex-start', sm: 'center', lg: 'flex-end' },
                      flexShrink: 0,
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',

                      '& .MuiToggleButtonGroup-grouped': {
                        borderColor: 'rgba(15, 23, 42, 0.08)',
                      },

                      '& .MuiToggleButton-root': {
                        minWidth: { xs: 44, sm: 48 },
                        px: { xs: 0.75, sm: 1 },
                        py: { xs: 0.35, sm: 0.45 },
                        fontSize: { xs: '0.66rem', sm: '0.72rem' },
                        fontWeight: 850,
                        textTransform: 'none',
                        color: '#334155',
                        borderColor: 'rgba(15, 23, 42, 0.08)',

                        '&.Mui-selected': {
                          bgcolor: '#0f172a',
                          color: '#fff',

                          '&:hover': {
                            bgcolor: '#111827',
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="today">Today</ToggleButton>
                    <ToggleButton value="last_7d">7d</ToggleButton>
                    <ToggleButton value="last_30d">30d</ToggleButton>
                    <ToggleButton value="this_month">Month</ToggleButton>
                    <ToggleButton value="last_90d">90d</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {error ? (
            <Alert severity="error" sx={{ borderRadius: '12px', flexShrink: 0 }}>
              {error}
            </Alert>
          ) : null}

          {success ? (
            <Alert severity="success" sx={{ borderRadius: '12px', flexShrink: 0 }}>
              {success}
            </Alert>
          ) : null}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'minmax(0, 2fr) minmax(320px, 1fr)',
              },
              gap: { xs: 0.75, md: 1.25 },
              flex: {
                xs: 'initial',
                md: 1,
              },
              minHeight: {
                xs: 'initial',
                md: 0,
              },
              width: '100%',
            }}
          >
            <Stack
              spacing={{ xs: 0.75, md: 1.25 }}
              sx={{
                height: {
                  xs: 'auto',
                  md: '100%',
                },
                minHeight: 0,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  height: {
                    xs: 300,
                    md: '40%',
                  },
                  minHeight: {
                    xs: 300,
                    md: 0,
                  },
                  minWidth: 0,
                }}
              >
                {this.renderEvolutionChart()}
              </Box>

              <Box
                sx={{
                  height: {
                    xs: selectedClient ? 560 : 440,
                    md: '60%',
                  },
                  minHeight: {
                    xs: selectedClient ? 560 : 440,
                    md: 0,
                  },
                  minWidth: 0,
                }}
              >
                {this.renderActivitySection()}
              </Box>
            </Stack>

            <Box
              sx={{
                height: {
                  xs: 640,
                  md: '100%',
                },
                minHeight: 0,
                minWidth: 0,
              }}
            >
              {this.renderClientPanel(clientSummary)}
            </Box>
          </Box>
        </Stack>
      </Box>
    );
  }
}

export default ClientCreditManagement;
